<template>
  <div class="search" v-if="isSearchOpen">
    <div class="search__container">
      <div class="search__content" v-click-outside="leave">
        <div class="search__form">
          <SearchSVG />
          <input 
            ref="input" 
            type="search" 
            class="search__form__input" 
            placeholder="Qual conhecimento você busca?" 
            @input="debounceInput" />
        </div>
      </div>

      <div class="search__result-container" v-if="courses.length || hasNoResults || isLoading"> 
        <div v-if="isLoading" class="search__loading">
          <LoadingIcon />
        </div>

        <div class="search__result"
          v-if="!isLoading"
        >
          <SearchResultCourse
            v-for="course in courses"
            :key="course.id"
            :course="course"
            @click.native="clickResult(course.id)"
          />
        </div>

        <div v-if="hasNoResults" class="text-center font-weight-light">
          Eita, não achamos resultados para essa pesquisa :(
        </div>
      </div>
    </div>

    <div class="search__overlay"></div>
  </div>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex'
  import { search } from '@/api/search.api'
  import SearchSVG from '@/components/svg/SearchSVG'
  import SearchResultCourse from '@/components/search/SearchResultCourse'
  import LoadingIcon from '@/components/layout/LoadingIcon'
  import _ from 'lodash'
  import ClickOutside from 'vue-click-outside'

  export default {
    directives: {
      ClickOutside,
    },
    
    components: {
      SearchSVG,
      SearchResultCourse,
      LoadingIcon,
    },

    data: () => ({
      isLoading: false,
      courses: [],
      hasNoResults: false,
      query: '',
    }),

    computed: {
      ...mapGetters('app', ['isSearchOpen'])
    },

    watch: {
      isSearchOpen (status) {
        document.body.style.overflow = status ? 'hidden' : 'auto';

        if (status) {
          this.$gtm.trackEvent({ event: 'search_open' })
          this.$nextTick(() => this.$refs.input.focus())
        }
      }
    },

    methods: {
      ...mapMutations('app', ['setSearchVisibility']),

      debounceInput: _.debounce(function(e) {
        this.query = e.target.value
        this.hasNoResults = false
        this.courses = []

        if (this.query.length > 2) {
          this.$gtm.trackEvent({ event: 'search', search_term: this.query })

          this.fetchSearchResult(this.query)
        }
      }, 500),

      async fetchSearchResult (query) {
        this.isLoading = true

        const res = await search(query)

        this.isLoading = false

        this.courses = res.data

        this.hasNoResults = !this.courses.length

        if (this.hasNoResults) {
          this.$gtm.trackEvent({ event: 'search_no_results', search_term : query })
        }
      },

      leave () {
        this.$gtm.trackEvent({ event: 'search_leave' })
        this.close()
      },

      clickResult (id) {
        this.$gtm.trackEvent({ event: 'search_select_content', item_id: id, content_type: 'course', search_term : this.quer })
        this.close()
      },

      close () {
        this.setSearchVisibility(false)
        this.courses = []
        this.hasNoResults = false
        this.isLoading = false
      },
    }
  }
</script>

<style lang="scss" scoped>
  @import "~@/assets/sass/variables";

  .search {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    
    &__container {
      position: relative;
      max-width: 600px;
      margin: auto;
      z-index: 101;
      padding: 0 0.9375rem;
      padding-top: 100px;

      @media(max-width: 550px) { 
        padding-top: 30px 
      }
    }

    &__content {
      background: #fff;
      border-radius: 24px;
      border: 3px solid $yellowBorder;
      background: $white400;
      box-shadow: 0px 4px 8px 2px #12001F, 0px 4px 4px 0px rgba(41, 29, 49, 0.20) inset, 0px 24px 32px -16px rgba(253, 212, 155, 0.25);
    }

    &__form {
      display: flex;
      align-items: center;
      padding: 5px 24px;

      &__input {
        border: 0;
        font-size: 1rem;
        color: $purple50;
        caret-color: $fucsia300;
        background: transparent;
        text-align: left;

        &::placeholder {
          text-align: center;
        }
      }
      
      ::v-deep svg { 
        width: 16px; 
        margin-right: 10px;

        path {
          fill: $fucsia300; 
        }
      }
    }

    &__loading { 
      padding: 20px 0 
    }

    &__result-container{
      padding: 24px;
      margin-top: 10px;
      max-height: calc(100vh - 18.75rem);
      overflow: auto;
      background: $purple85;
      border-radius: 8px;

      @media(max-width: 550px) { 
        max-height: calc(100vh - 170px) 
      }
    }

    &__loading {
      position: relative;
      padding: 20px 0;
    }

    &__overlay {
      position: fixed;
      background: rgba(#000, .6);
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 100;
    }
  }
</style>